import {normalize} from 'normalizr';
import {createAction, createAsyncThunk} from '@reduxjs/toolkit';
import {AppUserDto, mapiqApi} from '@lib/infrastructure';
import {NormalizedUserResult} from '../../infrastructure/auth/types';
import {AppThunkApiConfig} from '../../createStore';
import {AppUser, userEntity} from '../entities';
import {getIsProfilePicturesFeatureEnabled} from '../../infrastructure/features/selectors';
import {handleError} from '../../infrastructure/errorHandler';

export const postProfilePicture = createAsyncThunk<string, FormData, AppThunkApiConfig>(
  'user/postProfilePictures',
  async (data, {extra, dispatch, rejectWithValue}) => {
    const client = extra.apiClient;
    const api = mapiqApi(client);
    try {
      const {imageHash} = await api.postProfilePicture(data);
      return imageHash;
    } catch (err) {
      handleError(err, dispatch);

      if (err instanceof Error && err.message) {
        return rejectWithValue(err.message);
      }

      return rejectWithValue('Post profile picture failed');
    }
  },
  {
    condition: (_arg, {getState}) => {
      return getIsProfilePicturesFeatureEnabled(getState());
    },
  },
);

export const changeProfilePicture = createAsyncThunk<string, FormData, AppThunkApiConfig>(
  'user/putProfilePictures',
  async (data, {extra, dispatch, rejectWithValue}) => {
    const client = extra.apiClient;
    const api = mapiqApi(client);
    try {
      const {imageHash} = await api.putProfilePicture(data);
      return imageHash;
    } catch (err) {
      handleError(err, dispatch);

      if (err instanceof Error && err.message) {
        return rejectWithValue(err.message);
      }

      return rejectWithValue('Change profile picture failed');
    }
  },
  {
    condition: (_arg, {getState}) => {
      return getIsProfilePicturesFeatureEnabled(getState());
    },
  },
);

export const deleteProfilePicture = createAsyncThunk<void, void, AppThunkApiConfig>(
  'user/deleteProfilePictures',
  async (_arg, {extra, dispatch, rejectWithValue}) => {
    const client = extra.apiClient;
    const api = mapiqApi(client);
    try {
      await api.deleteProfilePicture();
    } catch (err) {
      handleError(err, dispatch);

      if (err instanceof Error && err.message) {
        return rejectWithValue(err.message);
      }

      return rejectWithValue('Delete profile picture failed');
    }
  },
  {
    condition: (_arg, {getState}) => {
      return getIsProfilePicturesFeatureEnabled(getState());
    },
  },
);

export const updateUser = createAsyncThunk<NormalizedUserResult, AppUser, AppThunkApiConfig>(
  'user/updateUser',
  async (user, {extra}) => {
    const {apiClient} = extra;
    const api = mapiqApi(apiClient);

    // Used only to update the preferred building and image hash at the moment.
    const response = await api.putUser(user as unknown as AppUserDto);
    const normalized = normalize<any, NormalizedUserResult>(response, userEntity);
    return normalized.entities;
  },
);

export const loadUser = createAsyncThunk<NormalizedUserResult, void, AppThunkApiConfig>(
  'user/loadUser',
  async (_, {extra}) => {
    const {apiClient} = extra;
    const api = mapiqApi(apiClient);
    const response = await api.getUser();
    const normalized = normalize<any, NormalizedUserResult>(response, userEntity);
    return normalized.entities;
  },
);

export const getBookingOverviewExportData = createAsyncThunk<
  {data: string; filename: string},
  {from: string; to: string} | undefined,
  AppThunkApiConfig
>('user/getBookingOverviewExport', async (options, {extra}) => {
  const {apiClient} = extra;
  const api = mapiqApi(apiClient);
  const {data, headers} = await api.getBookingOverviewExport(options);

  try {
    const {
      groups: {filename = ''},
    } = headers['content-disposition'].match(/filename=(?<filename>.*);/);

    return {
      data,
      filename,
    };
  } catch (err) {
    return {
      data,
      filename: 'booking_export.csv',
    };
  }
});

export const setIsNew = createAction<boolean>('user/setIsNew');
